/**
 * Ramp Embed - Stripe Utilities
 */
import { type Stripe } from "@stripe/stripe-js";
import { loadStripe } from "@stripe/stripe-js/pure";

import { type EmbedConfig, EmbedError, type IssuingProduct } from "./types";

type StripeRegion = "US" | "UK" | "EU" | "STABLECOIN";

const PRODUCT_TO_REGION: Partial<Record<IssuingProduct, StripeRegion>> = {
  STRIPE_USA: "US",
  STRIPE_FIFTH_THIRD_GOV_USA: "US",
  STRIPE_GOV_NON_FED_USA: "US",
  STRIPE_UK: "UK",
  STRIPE_UK_USD: "UK",
  STRIPE_UK_EUR: "UK",
  STRIPE_BG: "EU",
  STRIPE_DE: "EU",
  STRIPE_ES: "EU",
  STRIPE_FI: "EU",
  STRIPE_FR: "EU",
  STRIPE_HU: "EU",
  STRIPE_IE: "EU",
  STRIPE_IT: "EU",
  STRIPE_NL: "EU",
  STRIPE_PL: "EU",
  STRIPE_RO: "EU",
  STRIPE_SI: "EU",
  STRIPE_XBI_MX_USD: "STABLECOIN",
};

export function getStripePublishableKey(
  product: IssuingProduct,
  config: EmbedConfig
): string {
  const region = PRODUCT_TO_REGION[product];

  switch (region) {
    case "UK":
      return config.stripePublishableKeyUK;
    case "EU":
      return config.stripePublishableKeyEU;
    case "STABLECOIN":
      return config.stripePublishableKeyStablecoin;
    default:
      return config.stripePublishableKey;
  }
}

export async function loadStripeInstance(
  config: EmbedConfig,
  product?: IssuingProduct,
  stripeAccount?: string
): Promise<Stripe> {
  const key = product
    ? getStripePublishableKey(product, config)
    : config.stripePublishableKey;

  const stripe = await loadStripe(key, {
    stripeAccount: stripeAccount ?? undefined,
  });

  if (!stripe) {
    throw new EmbedError("CARD_DISPLAY_FAILED", "Failed to load card");
  }

  return stripe;
}

export async function generateNonce(
  stripe: Stripe,
  stripeCardId: string
): Promise<string> {
  const { nonce, error } = await stripe.createEphemeralKeyNonce({
    issuingCard: stripeCardId,
  });

  if (error || !nonce) {
    throw new EmbedError("NONCE_FAILED", "Failed to initialize card display");
  }

  return nonce;
}
